import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { MessageCircle, X, Send, Loader2, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ConversationalSidebar = ({ module, context = {}, onAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize with welcome message when opened
    if (isOpen && messages.length === 0) {
      const welcomeMsg = getWelcomeMessage(module);
      setMessages([{ role: 'assistant', content: welcomeMsg, timestamp: new Date() }]);
    }
  }, [isOpen, module, messages.length]);

  const getWelcomeMessage = (module) => {
    const welcome = {
      dashboard: "Hello! I'm your Maritime AI Assistant. I can help you analyze vessel data, check bunker prices, or monitor port congestion. What would you like to know?",
      trade_analytics: "Welcome to Trade Analytics! I can help you find arbitrage opportunities, analyze market trends, or generate reports. How can I assist you today?",
      insurance: "Hi! I'm here to help with insurance quotes, risk assessments, and policy management. What vessel or route would you like to analyze?",
      marketplace: "Welcome to the Marketplace! I can help you create RFQs, find vessel matches, or manage deals. What are you looking for?"
    };
    return welcome[module] || "Hello! How can I help you today?";
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ai/chat`,
        {
          message: input,
          module: module,
          context: context,
          conversation_history: messages.slice(-5) // Last 5 messages for context
        },
        { withCredentials: true }
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        actions: response.data.actions || [],
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('[AI Sidebar] Error:', error);
      toast.error('AI assistant temporarily unavailable');
      const errorMsg = {
        role: 'assistant',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (action) => {
    if (onAction) {
      onAction(action.type, action.params);
      toast.success(`Executing: ${action.label}`);
    }
  };

  return (
    <>
      {/* Toggle Button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed right-4 bottom-4 h-14 w-14 rounded-full shadow-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 z-50"
          data-testid="ai-chat-toggle"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Sidebar Panel */}
      {isOpen && (
        <div
          className="fixed right-0 top-0 h-full w-96 bg-[hsl(var(--card))] border-l shadow-2xl z-50 flex flex-col"
          data-testid="ai-sidebar"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              <span className="font-semibold">Maritime AI Assistant</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/80"
              data-testid="close-sidebar"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Module Badge */}
          <div className="px-4 py-2 border-b bg-[hsl(var(--secondary))]">
            <Badge variant="outline" className="text-xs">
              {module.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${idx}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg p-3 ${
                    msg.role === 'user'
                      ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
                      : 'bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  {msg.actions && msg.actions.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {msg.actions.map((action, actionIdx) => (
                        <Button
                          key={actionIdx}
                          size="sm"
                          variant="outline"
                          className="w-full"
                          onClick={() => handleAction(action)}
                          data-testid={`action-${actionIdx}`}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs opacity-70 mt-2">
                    {msg.timestamp.toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[hsl(var(--secondary))] rounded-lg p-3 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-[hsl(var(--background))]">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Ask me anything about maritime operations..."
                disabled={loading}
                className="flex-1"
                data-testid="ai-input"
              />
              <Button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                data-testid="ai-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
              Powered by Emergent LLM • Press Enter to send
            </p>
          </div>
        </div>
      )}
    </>
  );
};
